import {
  createSupplierSchema,
  supplierPaymentSchema,
  PAYMENT_METHODS,
} from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import { withCurrency, type Message, type Supplier } from './shared';

const blankSupplier = { name: '', contact: '', phone: '' };
const blankPayment = { amount: '', method: 'bank_transfer', reference: '' };

export function SuppliersTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(blankSupplier);
  const [payFor, setPayFor] = useState('');
  const [payment, setPayment] = useState(blankPayment);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const suppliers = useQuery({
    queryKey: ['inventory', schoolId, 'suppliers', page],
    queryFn: () =>
      apiList<Supplier>(`/schools/${schoolId}/inventory/suppliers`, {
        query: { page, pageSize: 20 },
      }).then(withCurrency),
  });
  const currency = suppliers.data?.currency ?? 'USD';

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['inventory', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const add = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/inventory/suppliers`, { method: 'POST', body }),
    onSuccess: () => {
      setForm(blankSupplier);
      setMessage({ kind: 'ok', text: t('inv.supplierAdded') });
      refresh();
    },
    onError: fail,
  });
  const pay = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/inventory/suppliers/${payFor}/payments`, { method: 'POST', body }),
    onSuccess: () => {
      setPayFor('');
      setPayment(blankPayment);
      setMessage({ kind: 'ok', text: t('inv.paid') });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('inv.tab.suppliers')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {suppliers.data?.data.length === 0 && (
        <p className="text-slate-600">{t('inv.noSuppliers')}</p>
      )}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {suppliers.data?.data.map((s) => (
          <li key={s.id} className="space-y-2 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="font-medium">{s.name}</span>
                {s.contactPerson ? ` · ${s.contactPerson}` : ''}
                {s.phone ? ` · ${s.phone}` : ''}
              </span>
              <span className="flex items-center gap-3">
                <span className={s.payableMinor > 0 ? 'font-medium' : 'text-slate-600'}>
                  {t('inv.owed', { amount: formatMinor(s.payableMinor, currency) })}
                </span>
                {can('inventory.pay', schoolId) && s.payableMinor > 0 && (
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('inv.pay')}: ${s.name}`}
                    onClick={() => setPayFor(payFor === s.id ? '' : s.id)}
                  >
                    {t('inv.pay')}
                  </button>
                )}
              </span>
            </div>
            {payFor === s.id && (
              <form
                noValidate
                aria-label={t('inv.payForm')}
                className="grid gap-3 rounded bg-slate-50 p-3 sm:grid-cols-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const amountMinor = parseMajor(payment.amount, currency);
                  const parsed = supplierPaymentSchema.safeParse({
                    amountMinor,
                    method: payment.method,
                    reference: payment.reference,
                  });
                  const next: Record<string, string> = {};
                  if (!parsed.success)
                    for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
                  if (amountMinor === null) next.amountMinor = t('inv.invalid');
                  setErrors(next);
                  if (!parsed.success || Object.keys(next).length > 0) return;
                  setMessage(null);
                  pay.mutate(parsed.data);
                }}
              >
                <TextField
                  label={t('inv.amount', { currency })}
                  inputMode="decimal"
                  value={payment.amount}
                  error={errors.amountMinor}
                  onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                />
                <div>
                  <label htmlFor="pay-method" className="block text-sm font-medium">
                    {t('inv.method')}
                  </label>
                  <select
                    id="pay-method"
                    className={selectClass}
                    value={payment.method}
                    onChange={(e) => setPayment({ ...payment, method: e.target.value })}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {t(`fees.method.${m}` as MessageKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <TextField
                  label={t('inv.reference')}
                  value={payment.reference}
                  error={errors.reference}
                  onChange={(e) => setPayment({ ...payment, reference: e.target.value })}
                />
                <div className="flex items-end">
                  <button type="submit" className={primaryButton} disabled={pay.isPending}>
                    {t('inv.payNow')}
                  </button>
                </div>
              </form>
            )}
          </li>
        ))}
      </ul>
      {suppliers.data && suppliers.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={suppliers.data.meta.totalPages} onPage={setPage} />
      )}

      {can('inventory.manage', schoolId) && (
        <form
          noValidate
          aria-label={t('inv.addSupplier')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createSupplierSchema.safeParse({
              name: form.name,
              contactPerson: form.contact,
              phone: form.phone,
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            add.mutate(parsed.data);
          }}
        >
          <TextField
            label={t('inv.supplierName')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('inv.contact')}
            value={form.contact}
            error={errors.contactPerson}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
          />
          <TextField
            label={t('inv.phone')}
            value={form.phone}
            error={errors.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={add.isPending}>
              {t('inv.addSupplier')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
